import React, { Component } from 'react';
import { Icon, Dropdown } from 'semantic-ui-react';
import { PropTypes } from 'prop-types';
import _ from 'lodash';
import { makeDebugger } from 'lib';

import './TagDropdown.css';

const debug = makeDebugger('Tag Dropdown');

class TagDropdown extends Component {
  state = {
    options: [],
    highlightedLabel: null,
    searchQuery: "",
    tagsSelected: []
  };

  static defaultProps = {
    handleTagsUpdated: this.handleTagsUpdated
  }

  constructor(props) {
    super(props);
    this.doneValue = "dropdown:close_action";
    this.closeValue = "editpanel:done_editing";
    this.focusSearchInput = this.focusSearchInput.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.handleAddition = this.handleAddition.bind(this);
    this.handleTagsUpdated = props.handleTagsUpdated
        || this.handleTagsUpdated.bind(this);
    this.handleTagOptionCreate = props.handleTagOptionCreate
        || this.handleTagOptionCreate.bind(this);
    this.getDropdownItem = props.dropdownItem || this.getDropdownItem;
    this.handleSearchUpdated = props.handleSearchUpdated;
  }

  // TODO add this to editpanel
  closeOnEsc = (e) => {
    if (e.keyCode === 27 && (!this.searchInput
          || this.searchInput !== document.activeElement)) {
      this.props.handleDone();
    }
  };

  componentDidMount() {
    document.addEventListener('keydown', this.closeOnEsc);
    const { tagOptions } = this.props;
    const options = tagOptions ? this.getOptionsWithActions(tagOptions) : [];
    this.setState({ options })
  }

  componentWillReceiveProps(nextProps) {
    document.removeEventListener('keydown', this.closeOnEsc);
    const optionsUpdated = !_.isEqual(nextProps.tagOptions, this.props.tagOptions);
    if (optionsUpdated) {
      this.setState({ options: this.getOptionsWithActions(nextProps.tagOptions)});
    }
  }

  getDropdownItem = (tag) => {
    return {
      key: tag,
      value: tag,
      text: tag,
      content: (
        <span>
          {tag}
          <span className="enter-prompt">press enter</span>
        </span>
      )
    }
  }

  getOptionsWithActions = (options) => {
    const { enableCloseAction, enableDoneAction } = this.props;
    let tags = options ? [ ...options ] : [];
    // Create a special item that allows close on selection
    enableCloseAction && tags.push({
      key: `**${this.closeValue}`,
      value: this.closeValue,
      className: 'dropdown-action-item close',
      content: (
        <span>
          Close Dropdown
          <span className="enter-prompt">press enter</span>
        </span>
      )
    });

    enableDoneAction && tags.push({
      key: `***${this.doneValue}`,
      value: this.doneValue,
      icon: 'checkmark',
      className: 'dropdown-action-item close',
      content: (
        <span>
          Done Editing
          <span className="enter-prompt">press enter</span>
        </span>
      )
    })

    tags = _.map(tags, (tag) => {
      return tag.value ? tag : this.getDropdownItem(tag);
    })
    return _.sortBy(tags, [tag => tag.key.toLowerCase()]);
  }

  handleTagOptionCreate = (tag) => {
    this.setState({
      options: [
        ...this.state.options,
        this.getDropdownItem(tag)
      ]
    })
  }

  handleTagsUpdated = (tags) => {
    this.setState({ tagsSelected: tags });
  }

  handleAddition = (e, { value }) => {
    const newValue = this.getTrimmedTag(value);
    this.handleTagOptionCreate(newValue);
    this.focusSearchInput();
  };

  // when user seelcts one of the tag labels in the search bar component
  handleLabelSelectOrDelete = (value) => {
    if (!this.state.selectedLabel && this.state.searchQuery.length === 0) {
      this.setState({ selectedLabel: value });
    } else if (this.state.selectedLabel !== value ) {
      this.setState({ selectedLabel: value })
    } else {
      const { tagsSelected } = this.props
      this.handleTagsUpdated(_.filter(tagsSelected, (t) => t !== value ));
      this.setState({ selectedLabel: null })
      this.focusSearchInput();
    }
  }

  handleLabelClick = (e, label) => {
    this.handleLabelSelectOrDelete(label.value);
  }

  renderLabel = label => ({
    color: 'black',
    content: `#${label.text}`,
    onRemove: null,
    onClick: this.handleLabelClick,
    as: 'a'
  })

  getTrimmedTag = (value) => {
    return value && value[0] === '#' ? value.slice(1) : value;
  }

  getNewValues = (values) => {
    const lastValue = _.last(values);
    const newValue = this.getTrimmedTag(lastValue);
    if (newValue !== lastValue) {
      const newValues = _.slice(values, 0, values.length - 1)
      newValues.push(newValue);
      return newValues;
    }
    return values;
  }

  handleChange = (e, data) => {
    debug('handle dropdown tag selection change: ', data.value);
    const newValue = _.last(data.value);
    if (newValue === this.closeValue) {
      // focus before blurring, otherwise the dropdown doesn't
      // close the menu upon blurring the input
      this.focusSearchInput();
      this.blurSearchInput();
      e.preventDefault();
    } else if (newValue === this.doneValue) {
      // focus before blurring, otherwise the dropdown doesn't
      // close the menu upon blurring the input
      this.focusSearchInput();
      this.blurSearchInput();
      this.props.handleDone() && this.props.handleDone();
    } else {
      const newValues = this.getNewValues(data.value);
      this.handleTagsUpdated(newValues);
      this.focusSearchInput(); // keeps input focused, even when elements are selected in menu
      this.setState({ searchQuery: "" });
    }
  }

  blurSearchInput = () => {
    this.searchInput && this.searchInput.blur();
    this.props.handleClose && this.props.handleClose();
  }

  focusSearchInput = () => {
    this.searchInput && this.searchInput.focus();
  }

  getCode(e) {
    return e.keyCode || e.which;
  }

  shouldHandleLabel = (e) => {
    return (e.key === 'Backspace' || this.getCode(e) === 8)
        && (!this.state.searchQuery || this.state.searchQuery.length === 0);
  }

  handleKeyDown = (e) => {
    if (this.shouldHandleLabel(e)) {
      const tagsSelected = this.props.tagsSelected || this.state.tagsSelected;
      this.handleLabelSelectOrDelete(_.last(tagsSelected));
      e.nativeEvent.stopImmediatePropagation();
    } else if (e.ctrlKey && e.key === 'Enter') { // ctrl + enter to close edit panel
      const { searchQuery } = this.state;
      if (searchQuery.length === 0) {
        this.blurSearchInput();
        this.props.handleDone(e);
      }
    }
    if (e.key !== 'Backspace' && this.state.selectedLabel) {
      this.setState({ selectedLabel: null })
    }
  }

  handleSearchChange = (e, data) => {
    this.handleSearchUpdated && this.handleSearchUpdated(data.searchQuery);
    this.setState({ searchQuery: data.searchQuery })
  }

  getSearchInputRef = (input) => {
    this.searchInput = input;
    this.searchInput && this.searchInput.addEventListener('blur', (e) => {
      this.props.handleClose && this.props.handleClose();
      e.preventDefault();
    })
    // this.focusSearchInput(); // Auto focus on mount
  }

  render() {
    return (
      <div
        onKeyDown={this.handleKeyDown}
        className={`tag-add-dropdown ${this.props.className}`}>
        <Dropdown
          className="left icon"
          compact
          fluid
          searchInput={{type: 'text', inputRef: this.getSearchInputRef}}
          options={this.state.options}
          value={this.props.tagsSelected || this.state.tagsSelected}
          placeholder={this.props.placeholder || 'Edit Tags'}
          search
          selection
          multiple
          floating
          closeOnChange={this.props.closeOnChange || false}
          onSearchChange={this.handleSearchChange}
          searchQuery={this.state.searchQuery}
          selectedLabel={this.state.selectedLabel}
          onClick={this.focusSearchInput}
          renderLabel={this.renderLabel}
          allowAdditions={this.props.allowAdditions}
          onAddItem={this.handleAddition}
          onChange={this.handleChange}
          noResultsMessage={this.props.noResultsMessage}
          // wrapSelection={false}
        />
        <Icon name="search" />
      </div>
    )
  }
}

TagDropdown.propTypes = {
  handleDone: PropTypes.func,
  // handleTagOptionCreate: PropTypes.func,
  handleTagsUpdated: PropTypes.func,
  tagOptions: PropTypes.arrayOf(PropTypes.string.isRequired),
  tagsSelected: PropTypes.arrayOf(PropTypes.string.isRequired),
  placeholder: PropTypes.string,
  handleAddition: PropTypes.bool
}

export default TagDropdown;



// WEBPACK FOOTER //
// ./src/components/TagDropdown/index.js